import React, { useEffect, useMemo, useState } from 'react';
import {
  ExtractionStep,
  ICD10Code,
  PcsFinalCode,
} from '../types';
import { ExtractionStatus } from '../types/multi-extraction';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { Button } from './animate-ui/components/buttons/button';
import { Collapsible } from './ui/collapsible';
import { motion } from 'framer-motion';
// ...existing imports
import { useTranslation } from '../i18n';
import { CopyIcon, ExclamationCircleIcon } from './Icons';
import { defineStepper } from './ui/stepper';
import { Spinner } from './ui/shadcn-io/spinner';
import { AnimateIcon } from './animate-ui/icons/icon';
import { LoaderPinwheel } from './animate-ui/icons/loader-pinwheel';

// Local lightweight animation helpers used in this file
export const ScaleIn: React.FC<{ children: React.ReactNode; delay?: number; className?: string }> = ({ children, delay = 0, className }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.98, y: 6 }}
    animate={{ opacity: 1, scale: 1, y: 0 }}
    transition={{ duration: 0.25, ease: 'easeOut', delay }}
    className={className}
  >
    {children}
  </motion.div>
);

export const StaggerContainer: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <motion.div
    variants={{
      hidden: { opacity: 1 },
      show: { opacity: 1, transition: { staggerChildren: 0.05 } },
    }}
    initial="hidden"
    animate="show"
    className={className}
  >
    {children}
  </motion.div>
);

export const StaggerItem: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <motion.div
    variants={{ hidden: { opacity: 0, y: 6 }, show: { opacity: 1, y: 0 } }}
    className={className}
  >
    {children}
  </motion.div>
);

import { ExtractionSession } from '../types/multi-extraction';

type StatusPanelProps = {
  connectionStatus: any;
  session: ExtractionSession | null;
  hoveredCodes?: string[] | null;
  onCodeHover?: (codes: string[] | null) => void;
  selectedCodes?: string[] | null;
  setSelectedCodes?: (c: string[] | null) => void;
  onRequestTabChange?: (tab: 'input' | 'extractions' | 'results') => void;
};

export const StatusPanel: React.FC<StatusPanelProps> = ({
  connectionStatus,
  session,
  hoveredCodes,
  onCodeHover
  , selectedCodes, setSelectedCodes
}) => {
  // DEBUG: log codeColors for troubleshooting savedSessions color mismatch
  if (session) {
    // eslint-disable-next-line no-console
    console.log('StatusPanel rendering session.codeColors ->', session.codeColors);
  }
  const [now, setNow] = useState<number>(Date.now());
  const { t } = useTranslation();

  // Stepperize: define steps once using the ExtractionStep enum
  const stepperDef = useMemo(() => {
    const steps = [
      { id: ExtractionStep.EXTRACTION_STARTED, title: t('status.extraction_started', 'Extraction Started') },
      { id: ExtractionStep.CONTEXT_BUILDING, title: t('status.context_building', 'Context Building') },
      { id: ExtractionStep.TEXT_PRUNING, title: t('status.text_pruning', 'Text Pruning') },
      { id: ExtractionStep.TEXT_SUMMARIZATION, title: t('status.text_summarization', 'Text Summarization') },
      { id: ExtractionStep.CONTEXT_COMPLETED, title: t('status.context_completed', 'Context Completed') },
      { id: ExtractionStep.QUERY_GENERATION, title: t('status.query_generation', 'Query Generation') },
      { id: ExtractionStep.QUERY_COMPLETED, title: t('status.query_completed', 'Query Completed') },
      { id: ExtractionStep.ICD10_SEARCH, title: t('status.icd10_search', 'ICD-10 Search') },
      { id: ExtractionStep.EXPANSION, title: t('status.expansion', 'Expansion') },
      { id: ExtractionStep.MINIMIZATION, title: t('status.minimization', 'Minimization') },
      { id: ExtractionStep.DEDUPLICATION, title: t('status.deduplication', 'Deduplication') },
      { id: ExtractionStep.SEARCH_COMPLETED, title: t('status.search_completed', 'Search Completed') },
      { id: ExtractionStep.CODE_SELECTION, title: t('status.code_selection', 'Code Selection') },
      { id: ExtractionStep.SELECTION_COMPLETED, title: t('status.selection_completed', 'Selection Completed') },
      { id: ExtractionStep.FINAL_PROCESSING, title: t('status.final_processing', 'Final Processing') },
      { id: ExtractionStep.MAIN_CODE_SELECTION, title: t('status.main_code_selection', 'Main Code Selection') },
      { id: ExtractionStep.MAIN_CODE_SELECTED, title: t('status.main_code_selected', 'Main Code Selected') },
      { id: ExtractionStep.EXTRACTION_COMPLETED, title: t('status.extraction_completed', 'Extraction Completed') },
      { id: 'pcs_started', title: t('status.pcs_started', 'PCS Extraction Started') },
      { id: 'pcs_completed', title: t('status.pcs_completed', 'PCS Extraction Completed') },
    ] as const;
    return defineStepper(...steps);
  }, [t]);

  const { useStepper, steps, utils } = stepperDef;
  const stepper = useStepper({ initialStep: ExtractionStep.EXTRACTION_STARTED });

  // Extract data from session
  const progress = session?.progress || [];
  const results = session?.results || null;
  const errors = session?.errors || [];
  const isExtracting = session?.status === ExtractionStatus.IN_PROGRESS;
  const codeColors = session?.codeColors || {};
  const pcsResults = results?.pcs ?? null;
  const pcsRan = !!results?.ran_pcs;
  const pcsFinalCodes = pcsResults?.final_codes ?? [];
  const diagnosticCodeCount = results?.codes?.length ?? 0;
  const pcsCodeCount = pcsFinalCodes.length;
  const totalCodesFound = diagnosticCodeCount + pcsCodeCount;
  const pcsProcessingTime = typeof pcsResults?.processing_time === 'number' ? pcsResults.processing_time : null;
  const pcsContextSummary = pcsResults?.context_summary?.trim();
  const pcsPrimaryQuery = pcsResults?.query?.trim();
  
  const extractionCompleted = !!results;
  const currentProgress = progress.length > 0 ? progress[progress.length - 1].progress_percent : 0;

  const formatTimestamp = (ts?: string | number) => {
    if (!ts) return '';
    const d = parseUTCDate(ts);
    return d ? d.toLocaleString() : '';
  };

  // Parse a timestamp we receive as UTC. If the string does not include
  // an explicit timezone we assume it's UTC and append a 'Z' so the JS
  // Date parser treats it as UTC instead of local time.
  function parseUTCDate(ts?: string | number | null) {
    if (!ts && ts !== 0) return null;
    if (typeof ts === 'number') return new Date(ts);
    if (typeof ts === 'string') {
      let s = ts.trim();
      // If it already has a timezone indicator (Z or +/-) let Date handle it
      if (/z$/i.test(s) || /[+-]\d{2}:?\d{2}$/.test(s)) {
        return new Date(s);
      }
      // Replace space separator with T, then append Z to force UTC
      s = s.replace(' ', 'T');
      if (!/t/i.test(s)) s = s.replace(' ', 'T');
      return new Date(s + 'Z');
    }
    // fallback
    try {
      return new Date(ts as any);
    } catch (e) {
      return null;
    }
  };

  // Find the latest (most recent) progress entry for a given stage.
  const findLatestProgressForStage = (stageId: string | number) => {
    for (let i = progress.length - 1; i >= 0; i--) {
      if (progress[i].stage === stageId) return { entry: progress[i], index: i };
    }
    return null;
  };

  // Compute duration in seconds for a step. Preference order:
  // 1. explicit step_duration in data
  // 2. difference between this step's timestamp and the next progress timestamp
  // 3. if this is the current running step, compute live duration from timestamp to now
  const computeStepDurationSeconds = (stageId: string | number) => {
    const found = findLatestProgressForStage(stageId);
    if (!found) return NaN;
    const { entry, index } = found;
    if (entry.data && typeof entry.data.step_duration === 'number' && isFinite(entry.data.step_duration)) {
      return entry.data.step_duration;
    }
    // look for next chronological entry after this one
    for (let j = index + 1; j < progress.length; j++) {
      const next = progress[j];
      if (next && next.timestamp) {
        const start = parseUTCDate(entry.timestamp);
        const end = parseUTCDate(next.timestamp);
        if (start && end) return (end.getTime() - start.getTime()) / 1000;
      }
    }
    // if it's the currently active step, compute live duration
    if (stepper.current && stepper.current.id === stageId && entry.timestamp) {
      const start = parseUTCDate(entry.timestamp);
      if (start) return (now - start.getTime()) / 1000;
    }
    return NaN;
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (e) {
      console.warn('Copy failed', e);
    }
  };

  const isCssColor = (v?: string) => {
    if (!v) return false;
    const s = v.trim();
    if (s.startsWith('#')) return /^#[0-9A-Fa-f]{3,8}$/.test(s);
    if (s.startsWith('rgb') || s.startsWith('hsl')) return true;
    const keywords = ['transparent','currentColor','black','white','red','blue','green','yellow','gray'];
    if (keywords.includes(s.toLowerCase())) return true;
    return false;
  };

  const isTailwindClass = (v?: string) => {
    if (!v) return false;
    return /(^|\s)(bg-|dark:|text-|ring-|from-|via-|to-)/.test(v);
  };

  const hashToHue = (s: string) => {
    let hash = 0;
    for (let i = 0; i < s.length; i++) {
      hash = (hash << 5) - hash + s.charCodeAt(i);
      hash |= 0;
    }
    return Math.abs(hash) % 360;
  };

  const fallbackCssColorForCode = (code?: string) => {
    const c = String(code || '').trim();
    if (!c) return undefined;
    const hue = hashToHue(c);
    return `hsl(${hue}, 65%, 72%)`;
  };

  const renderedPcsFinalCodes = pcsFinalCodes.map((code: PcsFinalCode, index: number) => {
    const confidence = typeof code.confidence === 'number'
      ? Math.max(0, Math.min(1, code.confidence))
      : null;
    const refs = (code as any)?.text_references;
    return (
      <div
        key={`${code.code7}-${index}`}
        className="border border-border/60 rounded-md bg-muted/10 p-3 space-y-2"
      >
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {(() => {
            const pcsKey = String(code.code7 || code.code4 || '').trim();
            const raw = pcsKey ? codeColors[pcsKey] : undefined;
            if (isCssColor(raw)) {
              return <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: raw }} />;
            }
            if (raw && isTailwindClass(raw)) {
              return <span className={`inline-block w-3 h-3 rounded-sm ${raw}`} />;
            }
            const fallback = fallbackCssColorForCode(pcsKey);
            return <span className="inline-block w-3 h-3 rounded-sm" style={fallback ? { backgroundColor: fallback } : undefined} />;
          })()}
          <span className="font-mono text-sm font-semibold text-primary">
            {code.code7}
          </span>
          <Badge variant="outline" className="uppercase tracking-wide">
            {t('status.pcs_code4', 'Root')}: {code.code4}
          </Badge>
          <Badge variant="outline" className="uppercase tracking-wide">
            {t('status.pcs_approach', 'Approach')}: {code.approach || '-'}
          </Badge>
          <Badge variant="outline" className="uppercase tracking-wide">
            {t('status.pcs_device', 'Device')}: {code.device || '-'}
          </Badge>
          <Badge variant="outline" className="uppercase tracking-wide">
            {t('status.pcs_qualifier', 'Qualifier')}: {code.qualifier || '-'}
          </Badge>
          {confidence !== null && (
            <Badge variant="outline" className="uppercase tracking-wide">
              {t('status.confidence', 'Confidence:')} {Math.round(confidence * 100)}%
            </Badge>
          )}
        </div>
        {code.rationale && (
          <p className="text-xs text-muted-foreground whitespace-pre-wrap">
            {code.rationale}
          </p>
        )}

        {Array.isArray(refs) && refs.length > 0 && (
          <div className="text-xs text-muted-foreground">
            <div className="font-medium">{t('status.references', 'References:')}</div>
            <ul className="list-disc pl-5 mt-1">
              {refs.slice(0, 3).map((r: any, i: number) => (
                <li key={i} className="break-words">{String((r?.text_snippet || r?.text || '')).trim()}</li>
              ))}
            </ul>
          </div>
        )}
        {code.source && (
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
            {t('status.pcs_source', 'Source')}: {code.source}
          </p>
        )}
      </div>
    );
  });


  useEffect(() => {
    // Tick while extraction is in progress (there's an active step)
    const active = progress.length > 0 && !extractionCompleted;
    if (!active) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [progress.length, extractionCompleted]);

  // Sync stepper current step to latest progress
  useEffect(() => {
    if (progress.length === 0) return;
    const current = progress[progress.length - 1];
    if (current?.stage) {
      stepper.goTo(current.stage);
    }
  }, [progress, stepper]);

  const formatDurationFromSeconds = (totalSeconds: number) => {
    if (!isFinite(totalSeconds) || totalSeconds < 0) return '0s';
    const sec = Math.floor(totalSeconds % 60);
    const min = Math.floor((totalSeconds / 60) % 60);
    const hrs = Math.floor(totalSeconds / 3600);
    if (hrs > 0) return `${hrs}h ${String(min).padStart(2, '0')}m`;
    if (min > 0) return `${min}m ${String(sec).padStart(2, '0')}s`;
    return `${sec}s`;
  };

  // If no session is selected, render an empty spacer to keep layout centered
  if (!session) {
    return (
      <div className="w-full max-w-md mx-auto h-48" />
    );
  }

  return (
    <div className="w-full max-w-md mx-auto space-y-6 flex flex-col justify-center items-center min-h-[60vh]">
      <span aria-hidden className="hidden">{String(connectionStatus)}</span>
      {/* Progress Overview */}
      {isExtracting && !extractionCompleted && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle>{t('status.extraction_progress', 'Extraction Progress')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex justify-between text-sm text-muted-foreground mb-2">
                <span>{t('status.overall_progress', 'Overall Progress')}</span>
                <span>{currentProgress}%</span>
              </div>
              <Progress value={currentProgress} className="h-2" />
            </div>

            {progress.length > 0 && (
              <div className="text-sm">
                <div className="font-medium">
                  {progress[progress.length - 1].message}
                </div>
                <div className="text-muted-foreground">
                  {formatTimestamp(progress[progress.length - 1].timestamp)}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Extraction Steps (Stepper) */}
      {progress.length > 0 && !extractionCompleted && (
        <div className="bg-card rounded-lg border p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium">{t('status.extraction_steps', 'Extraction Steps')}</div>
            <div className="text-xs text-muted-foreground">{isExtracting ? t('status.in_progress', 'In Progress') : results ? t('status.completed', 'Completed') : ''}</div>
          </div>

          <nav aria-label="Extraction Steps Navigation" className="group my-2">
            <ol className="flex flex-col gap-2" aria-orientation="vertical">
              {stepper.all.map((step, index, array) => (
                <React.Fragment key={step.id}>
                  <li className="flex items-center gap-4 flex-shrink-0">
                    <Button
                      type="button"
                      role="tab"
                      variant={index <= utils.getIndex(stepper.current.id) ? 'default' : 'secondary'}
                      aria-current={stepper.current.id === step.id ? 'step' : undefined}
                      aria-posinset={index + 1}
                      aria-setsize={steps.length}
                      aria-selected={stepper.current.id === step.id}
                      className="flex items-center justify-center rounded-full w-7 h-7"
                      onClick={() => stepper.goTo(step.id)}
                    >
                      {stepper.current.id === step.id && isExtracting ? (
                        <AnimateIcon animateOnHover>
                          <LoaderPinwheel />
                        </AnimateIcon>
                      ) : (
                        index + 1
                      )}
                    </Button>
                    <span className={`text-sm ${progress.find((p) => p.stage === step.id) ? 'font-medium' : 'text-muted-foreground'}`}>{step.title}</span>
                  </li>
                  <div className="flex gap-4">
                    {index < array.length - 1 && (
                      <div className="flex justify-center" style={{ paddingInlineStart: '1.25rem' }}>
                        <div className={`w-[1px] h-full ${index < utils.getIndex(stepper.current.id) ? 'bg-primary' : 'bg-muted'}`} />
                      </div>
                    )}
                    <div className="flex-1 my-4">
                      {stepper.current.id === step.id && stepper.switch({
                        // For each step id, return the panel that shows timestamp/progress/duration
                        ...(steps.reduce((acc, s) => {
                          acc[s.id as any] = () => {
                            const sp = progress.find((p) => p.stage === s.id);
                            if (!sp) return <div className="text-xs text-muted-foreground">{t('status.no_progress_step', 'No progress for this step yet.')}</div>;
                            const isCurr = stepper.current.id === s.id;
                            return (
                              <div className="text-xs text-muted-foreground">
                                <div className="flex items-center justify-between">
                                  <div>{formatTimestamp(sp.timestamp)}</div>
                                  <Badge variant="secondary" className="text-xs ml-2">{sp.progress_percent}%</Badge>
                                </div>
                                {(() => {
                                  const dur = computeStepDurationSeconds(s.id);
                                  const hasDur = Number.isFinite(dur) && !isNaN(dur);
                                  if (isCurr) {
                                    // show live running duration with spinner
                                    return (
                                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                        <span className="inline-flex items-center">
                                          <Spinner variant="circle" size={16} />
                                        </span>
                                        <span>{t('status.running', 'Running')}: {hasDur ? formatDurationFromSeconds(dur) : t('status.unknown_duration', 'Calculating...')}</span>
                                      </div>
                                    );
                                  }
                                  if (hasDur) {
                                    return <div>{t('status.duration', 'Duration:')} {formatDurationFromSeconds(dur)}</div>;
                                  }
                                  return <div className="text-xs text-muted-foreground">{t('status.no_progress_step', 'No progress for this step yet.')}</div>;
                                })()}
                              </div>
                            );
                          };
                          return acc;
                        }, {} as Record<string, () => React.ReactNode>))
                      })}
                    </div>
                  </div>
                </React.Fragment>
              ))}
            </ol>
          </nav>
        </div>
      )}

      {/* Results Summary */}
      {results && (
        <div className="sticky top-4 z-20 self-start">
          <Card className="my-7">
            <CardHeader className="pb-3">
              <CardTitle>{t('status.results', 'Results')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('status.codes_found', 'Codes Found:')}</span>
                  <Badge variant="secondary">{totalCodesFound}</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('status.processing_time', 'Processing Time:')}</span>
                  <span className="font-medium">{results.processing_time.toFixed(2)}s</span>
                </div>
                {pcsRan && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('status.pcs_codes_found', 'PCS Codes Found:')}</span>
                    <Badge variant="secondary">{pcsFinalCodes.length}</Badge>
                  </div>
                )}
                {pcsRan && pcsProcessingTime !== null && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('status.pcs_processing_time', 'PCS Processing Time:')}</span>
                    <span className="font-medium">{pcsProcessingTime.toFixed(2)}s</span>
                  </div>
                )}
                {results.metadata?.confidence_score && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('status.confidence', 'Confidence:')}</span>
                    {(() => {
                      const pct = Math.max(0, Math.min(1, results.metadata!.confidence_score));
                      const hue = Math.round(30 + (140 - 30) * pct);
                      const bg = `hsl(${hue} 70% 45%)`;
                      const textColor = '#fff';
                      return <Badge variant="success" style={{ background: bg, color: textColor, borderColor: 'transparent' }}>{(pct * 100).toFixed(1)}%</Badge>;
                    })()}
                  </div>
                )}
              </div>

              <div className="flex">
                <button
                  type="button"
                  className="w-full inline-flex items-center gap-2 text-sm text-muted-foreground bg-muted/10 border border-border rounded-full px-3 py-1 hover:bg-muted/20 transition-colors"
                  onClick={() => {
                    if (!results && pcsFinalCodes.length === 0) return;
                    const diagnosticos = (results?.codes ?? [])
                      .map((c: ICD10Code) => c.code)
                      .filter((code): code is string => !!code);
                    const procedimientos = pcsFinalCodes
                      .map((code: PcsFinalCode) => code.code7 || code.code4)
                      .filter((code): code is string => !!code);
                    if (diagnosticos.length === 0 && procedimientos.length === 0) return;
                    copyToClipboard(JSON.stringify({ diagnosticos, procedimientos }));
                  }}
                >
                  <CopyIcon className="w-4 h-4" />
                  {t('status.copy_all_codes', 'Copy All Codes')}
                </button>
              </div>
              {results.codes.length > 0 && (
                <div>
                  <h4 className="font-medium mb-3">{t('status.icd10_codes', 'ICD-10 Codes:')}</h4>
                  <StaggerContainer className="space-y-2">
                    {results.codes.map((code: ICD10Code, index: number) => (
                      <StaggerItem key={index}>
                        <motion.div
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                          whileHover={{ scale: 1.01 }}
                          className={`group rounded-md overflow-hidden transform transition-all duration-200 ${((hoveredCodes && hoveredCodes.includes(code.code)) || (selectedCodes && selectedCodes.includes(code.code))) ? 'border-2 border-primary bg-card/95 ring-2 ring-primary ring-offset-1 scale-102 shadow-lg' : 'border border-border bg-card hover:scale-101 hover:shadow-sm'}`}
                          onMouseEnter={() => onCodeHover && onCodeHover([code.code])}
                          onMouseLeave={() => onCodeHover && onCodeHover(null)}
                          onClick={() => {
                            if (!setSelectedCodes) return;
                            const isSelected = !!(selectedCodes && selectedCodes.includes(code.code));
                            if (isSelected) {
                              const next = (selectedCodes || []).filter(c => c !== code.code);
                              setSelectedCodes(next.length > 0 ? next : null);
                            } else {
                              const next = Array.from(new Set([...(selectedCodes || []), code.code]));
                              setSelectedCodes(next);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          <Collapsible
                            title={
                              <div className="flex items-center gap-2">
                                {(() => {
                                  const raw = codeColors[code.code];
                                  if (isCssColor(raw)) {
                                    return <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: raw }} />;
                                  }
                                  // fallback to tailwind class or default
                                  const cls = raw && isTailwindClass(raw) ? raw : 'bg-primary';
                                  return <span className={`inline-block w-3 h-3 rounded-sm ${cls}`} />;
                                })()}
                                <div className="font-mono text-primary font-medium flex items-center gap-2">
                                  <span>{code.code}</span>
                                  <button
                                    type="button"
                                    aria-label={`Copy ${code.code}`}
                                    onClick={(e) => { e.stopPropagation(); copyToClipboard(code.code); }}
                                    className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-muted/10 border border-border text-muted-foreground hover:bg-muted/20 hover:text-foreground transition-colors"
                                  >
                                    <CopyIcon className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                                <div className="flex-1" />
                                {code.confidence && (
                                  (() => {
                                    const pct = Math.max(0, Math.min(1, code.confidence));
                                    // make 50% orange: pct <= 0.5 -> orange (~30). For >50% interpolate to green (~140)
                                    let hue: number;
                                    if (pct <= 0.5) {
                                      hue = 30; // orange
                                    } else {
                                      const t = (pct - 0.5) / 0.5; // normalize 0.5..1 -> 0..1
                                      hue = Math.round(30 + (140 - 30) * t);
                                    }
                                    const bg = `hsl(${hue} 70% 45%)`;
                                    const textColor = '#fff';
                                    return (
                                      <Badge variant="outline" className="mx-2 text-xs" style={{ background: bg, color: textColor, borderColor: 'transparent' }}>
                                        {Math.round(pct * 100)}%
                                      </Badge>
                                    );
                                  })()
                                )}
                              </div>
                            }
                            defaultOpen={false}
                            className="bg-transparent"
                            headerClassName="bg-muted/10 dark:bg-muted/60 group-hover:bg-muted/20"
                          >
                            <div className="text-muted-foreground text-sm p-4">
                              <div className="mb-2">{code.explanation || code.description}</div>
                              {code.text_references && code.text_references.length > 0 && (
                                <div className="text-xs text-muted-foreground">
                                  <div className="font-medium">{t('status.references', 'References:')}</div>
                                  <ul className="list-disc pl-5 mt-1">
                                    {code.text_references.map((r: any, i: number) => (
                                      <li key={i} className="break-words">{(r.text_snippet || r.text || '').trim()}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          </Collapsible>
                        </motion.div>
                      </StaggerItem>
                    ))}
                  </StaggerContainer>
                </div>
              )}
              {pcsRan && (
                <div className="pt-6 border-t border-border/60 space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">{t('status.pcs_codes_header', 'ICD-10-PCS Codes')}</h4>
                    <Badge variant="secondary">{pcsFinalCodes.length}</Badge>
                  </div>
                  {pcsFinalCodes.length > 0 ? (
                    <>
                      <div className="space-y-2">
                        {renderedPcsFinalCodes}
                      </div>
                      <div className="flex">
                        <button
                          type="button"
                          className="w-full inline-flex items-center gap-2 text-sm text-muted-foreground bg-muted/10 border border-border rounded-full px-3 py-1 hover:bg-muted/20 transition-colors"
                          onClick={() => copyToClipboard(pcsFinalCodes.map((c) => c.code7).join(', '))}
                        >
                          <CopyIcon className="w-4 h-4" />
                          {t('status.copy_all_pcs_codes', 'Copy All PCS Codes')}
                        </button>
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {t('status.pcs_none', 'No procedure codes were finalized by the PCS agent.')}
                    </p>
                  )}
                  {pcsPrimaryQuery && (
                    <p className="text-xs text-muted-foreground">
                      <span className="font-medium uppercase tracking-wide mr-1">
                        {t('status.pcs_primary_query', 'Primary Query')}:
                      </span>
                      {pcsPrimaryQuery}
                    </p>
                  )}
                  {pcsContextSummary && (
                    <div className="text-xs text-muted-foreground space-y-1">
                      <div className="font-medium uppercase tracking-wide">
                        {t('status.pcs_context', 'Context Summary')}
                      </div>
                      <p className="whitespace-pre-wrap">{pcsContextSummary}</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Errors */}
      {errors.length > 0 && (
        <Card className="border-destructive/50 bg-destructive/5">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center space-x-2 text-destructive">
              <ExclamationCircleIcon className="w-5 h-5" />
              <span>{t('status.errors', 'Errors')}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {errors.map((error, index) => (
                <Collapsible
                  key={index}
                  title={
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium text-destructive">{error.message}</div>
                      <div className="text-xs text-muted-foreground">{formatTimestamp(error.timestamp)}</div>
                    </div>
                  }
                  defaultOpen={false}
                >
                  <div className="text-sm text-destructive">{t('status.no_additional_details', 'No additional details.')}</div>
                  {error.code && <div className="mt-2"><Badge variant="outline" className="text-xs">{error.code}</Badge></div>}
                </Collapsible>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
